'use client';

import { useTranslation } from '@/context/LanguageContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFlag, faBuilding, faAward, faStar, faGraduationCap, faGlobe, faRocket, faTree } from '@fortawesome/free-solid-svg-icons';

interface TimelineItem {
    year: string;
    contentIdn: string;
    contentEn: string;
}

interface TimelineSectionClientProps {
    items: TimelineItem[];
}

export default function TimelineSectionClient({ items }: TimelineSectionClientProps) {
    const { t, language } = useTranslation();

    // Default static items as fallback
    const defaultItems: TimelineItem[] = [
        {
            year: '1962',
            contentIdn: '<h4 class="text-xl font-bold text-gray-800 mt-2">Pendirian UNMUL</h4><p class="text-gray-600 mt-2">Universitas Mulawarman resmi berdiri dengan 3 fakultas perdana: Ekonomi, Pertanian, dan Kehutanan.</p>',
            contentEn: '<h4 class="text-xl font-bold text-gray-800 mt-2">Establishment of UNMUL</h4><p class="text-gray-600 mt-2">Mulawarman University was officially established with 3 inaugural faculties: Economics, Agriculture, and Forestry.</p>'
        },
        {
            year: '1982',
            contentIdn: '<h4 class="text-xl font-bold text-gray-800 mt-2">Relokasi ke Kampus Gunung Kelua</h4><p class="text-gray-600 mt-2">Pembangunan kampus terpadu di kawasan Gunung Kelua, Samarinda dengan luas area 750 hektar.</p>',
            contentEn: '<h4 class="text-xl font-bold text-gray-800 mt-2">Relocation to Gunung Kelua Campus</h4><p class="text-gray-600 mt-2">Development of the integrated campus in the Gunung Kelua area, Samarinda with an area of ​​750 hectares.</p>'
        },
        {
            year: '2000',
            contentIdn: '<h4 class="text-xl font-bold text-gray-800 mt-2">Akreditasi Nasional</h4><p class="text-gray-600 mt-2">UNMUL mencapai akreditasi A untuk pertama kalinya dari BAN-PT untuk beberapa program studi unggulan.</p>',
            contentEn: '<h4 class="text-xl font-bold text-gray-800 mt-2">National Accreditation</h4><p class="text-gray-600 mt-2">UNMUL achieved A accreditation for the first time from BAN-PT for several superior study programs.</p>'
        },
        {
            year: '2024',
            contentIdn: '<h4 class="text-xl font-bold text-gray-800 mt-2">Menuju IKN & World Class</h4><p class="text-gray-600 mt-2">Menjadi mitra strategis pembangunan Ibu Kota Nusantara dengan fokus pada riset tropis dan keberlanjutan.</p>',
            contentEn: '<h4 class="text-xl font-bold text-gray-800 mt-2">Towards IKN & World Class</h4><p class="text-gray-600 mt-2">Becoming a strategic partner in the development of the Nusantara Capital City with a focus on tropical research and sustainability.</p>'
        }
    ];

    const displayItems = items.length > 0 ? items : defaultItems;

    // Icon pool to cycle through
    const icons = [faFlag, faBuilding, faAward, faStar, faGraduationCap, faGlobe, faRocket, faTree];

    return (
        <div className="relative mt-20">
            <h3 className="text-3xl font-serif font-bold text-center text-gray-900 mb-16" data-aos="fade-up">
                {t('common.journey') || 'Perjalanan'} <span className="text-unmulGreen">{t('common.us') || 'Kami'}</span>
            </h3>

            {/* Center Line - Visible on md and up, starts below title */}
            <div className="hidden md:block absolute left-1/2 top-24 bottom-0 w-1 bg-gradient-to-b from-unmulGreen via-unmulYellow to-unmulGreen transform -translate-x-1/2 rounded-full"></div>

            <div className="space-y-12 relative">
                {displayItems.map((item, index) => {
                    const isLeft = index % 2 === 0;
                    const icon = icons[index % icons.length];
                    const content = language === 'id' ? item.contentIdn : item.contentEn;

                    return (
                        <div key={item.year} className="flex flex-col md:flex-row items-center" data-aos="fade-up">
                            {/* Left Side */}
                            <div className={`md:w-1/2 md:pr-12 ${isLeft ? 'md:text-right' : 'hidden md:block'}`}>
                                {isLeft && (
                                    <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-unmulGreen md:border-l-0 md:border-r-4">
                                        <span className="text-unmulYellow font-bold text-3xl">{item.year}</span>
                                        <div dangerouslySetInnerHTML={{ __html: content }} />
                                    </div>
                                )}
                            </div>

                            {/* Center Icon */}
                            <div className={`w-12 h-12 ${isLeft ? 'bg-unmulGreen text-white' : 'bg-unmulYellow text-unmulDarkGreen'} rounded-full flex items-center justify-center shadow-lg z-10 my-4 md:my-0 flex-shrink-0`}>
                                <FontAwesomeIcon icon={icon} />
                            </div>

                            {/* Right Side */}
                            <div className={`md:w-1/2 md:pl-12 ${!isLeft ? '' : 'hidden md:block'}`}>
                                {!isLeft && (
                                    <div className={`bg-white p-6 rounded-xl shadow-lg border-l-4 ${isLeft ? 'border-unmulGreen' : 'border-unmulYellow'}`}>
                                        <span className={`text-${isLeft ? 'unmulYellow' : 'unmulGreen'} font-bold text-3xl`}>{item.year}</span>
                                        <div dangerouslySetInnerHTML={{ __html: content }} />
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
